import { createSlice } from '@reduxjs/toolkit';
import { TransactionDocumentsState } from './transaction-documents.state';
import { GetTransactionDocuments } from './thunks/get-transaction-documents.thunk';
import { UploadTransactionDocument } from './thunks/upload-transaction-document.thunk';
import { SaveRequiredDocumentTypes } from './thunks/save-required-document-types.thunk';
import { AddCommonDocumentType } from './thunks/add-common-document-type.thunk';
import { StoreState } from '../../../store/store-state';

const isResponseForCurrentSelection = (
    state: TransactionDocumentsState,
    respondedEngagementId: number
): boolean =>
    state.transactionDocumentsRequestedEngagementId === undefined ||
    state.transactionDocumentsRequestedEngagementId === respondedEngagementId;

const initialState: TransactionDocumentsState = {
    transactionDocuments: undefined,
    transactionDocumentsEngagementId: undefined,
    transactionDocumentsRequestedEngagementId: undefined,
    transactionDocumentsLoading: false,
    transactionDocumentsError: '',
};

const transactionDocumentsSlice = createSlice({
    name: 'transactionDocuments',
    initialState,
    reducers: {
        resetTransactionDocuments() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(GetTransactionDocuments.pending, (state, action) => {
                state.transactionDocumentsRequestedEngagementId = action.meta.arg;
                const isRefreshOfLoadedEngagement =
                    state.transactionDocuments !== undefined &&
                    state.transactionDocumentsEngagementId === action.meta.arg;
                state.transactionDocumentsLoading = !isRefreshOfLoadedEngagement;
                state.transactionDocumentsError = '';
            })
            .addCase(GetTransactionDocuments.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg)) {
                    return;
                }
                state.transactionDocuments = action.payload;
                state.transactionDocumentsEngagementId = action.meta.arg;
                state.transactionDocumentsLoading = false;
                state.transactionDocumentsError = '';
            })
            .addCase(GetTransactionDocuments.rejected, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg)) {
                    return;
                }
                state.transactionDocumentsLoading = false;
                state.transactionDocumentsRequestedEngagementId = undefined;
                state.transactionDocumentsError =
                    action.error.message || 'Failed to fetch the transaction documents';
            })
            .addCase(UploadTransactionDocument.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                state.transactionDocuments = action.payload;
            })
            .addCase(SaveRequiredDocumentTypes.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                state.transactionDocuments = action.payload;
            })
            .addCase(AddCommonDocumentType.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                state.transactionDocuments = action.payload;
            });
    },
});

export const selectTransactionDocuments = (state: StoreState, engagementId: number) =>
    state.transactionDocuments.transactionDocumentsEngagementId === engagementId
        ? state.transactionDocuments.transactionDocuments
        : undefined;
export const selectTransactionDocumentsLoading = (state: StoreState) =>
    state.transactionDocuments.transactionDocumentsLoading;
export const selectTransactionDocumentsRequested = (
    state: StoreState,
    engagementId: number
) =>
    state.transactionDocuments.transactionDocumentsRequestedEngagementId === engagementId;

export default transactionDocumentsSlice.reducer;
